import React, { useEffect, useMemo, useState, useCallback } from "react";
import { FormAnswerField } from "../../../../../../interfaces/FormAnswerField";
import ItemRegister, { ComboItem } from "../../../../../ItemRegister";
import { Card } from "../../../../../../interfaces/Card";
import formAnswerToComboItemFromFlow from "../../../../../../utils/formAnswerToComboItemFromFlow";
import { Flow } from "../../../../../../interfaces/Flow";
import { ContainerProgressBar, ProgressBarTask, ProgressBarTaskLine, ProgressBarTaskLineInside, ProgressBarTaskPercent, ViewToggleButton, ViewToggleContainer } from "../style";
import { BsGrid, BsList } from "react-icons/bs";
import ComboBoxFlowTable from "../ComboBoxFlowTable";
import { styled, Tooltip, TooltipProps } from "@material-ui/core";
import { useCardDialog } from "../../../../../../hooks/cardDialogContext";
import api from "../../../../../../services/api";
import { useToast } from "../../../../../../hooks/toast";

interface ComboBoxFlowStepViewProps {
    values: FormAnswerField[];
    flow_base_id: number;
    type_view?: string;
}

const DarkTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
    "& .MuiTooltip-tooltip.MuiTooltip-tooltipPlacementBottom": {
        backgroundColor: "#1F2D32",
        color: 'white',
        boxShadow: theme.shadows[1],
        fontSize: 12,
    },
}));

const ComboBoxFlowView: React.FC<ComboBoxFlowStepViewProps> = ({ values, type_view, flow_base_id }) => {

    //Function to compare and sort the items
    function compareItems(a: ComboItem, b: ComboItem): number {
        // Critério 1: Items com o campo completed igual a "S"
        if (a.complete === 'S' && b.complete !== 'S') {
            return 1; // a vem antes de b
        }
        if (a.complete !== 'S' && b.complete === 'S') {
            return -1; // a vem depois de b
        }

        // Critério 3: Items com o campo due_date_completed mais antigos
        if (a.due_date_completed && b.due_date_completed) {
            const dateA = new Date(a.due_date_completed).getTime();
            const dateB = new Date(b.due_date_completed).getTime();
            return dateA - dateB; // Ordenação ascendente por data
        }

        // Critério 2: Items com o campo due_date_completed igual a null ou undefined
        if (!a.due_date_completed && b.due_date_completed) {
            return 1; // a vem antes de b
        }
        if (a.due_date_completed && !b.due_date_completed) {
            return -1; // a vem depois de b
        }

        if (a.id_register !== undefined && b.id_register !== undefined) {
            return a.id_register - b.id_register;
        }

        // Se todos os critérios forem iguais, mantém a ordem original
        return 0;

    }

    const { openCard } = useCardDialog();
    const { addToast } = useToast();

    const [itemSelected, setItemSelected] = useState<ComboItem[]>([]);
    const [flow, setFlow] = useState<Flow>();
    const [percentage, setPercentage] = useState<number>();
    const [typeView, setTypeView] = useState<string | undefined>(type_view !== undefined && type_view !== null ? type_view : "C");

    const cards = useMemo<Card[]>(
        () => itemSelected
            .map(({ card }) => card)
            .filter((c): c is Card => c !== undefined),
        [itemSelected]);

    const handleToggleView = useCallback(() => {
        setTypeView(prev => prev === "C" ? "L" : "C");
    }, []);

    const fetchCards = useCallback(async (card_id?: number) => {
        if (flow === undefined) return;

        try {
            // If card_id is provided, only update that specific card
            if (card_id !== undefined) {
                // Find the card in itemSelected that needs to be updated
                const cardIndex = itemSelected.findIndex(item => item.card?.id_card === card_id);
                if (cardIndex === -1) return;

                const response = await api.post(`/card/by-cards`, {
                    card_items: [card_id],
                    flow_id: flow.id_flow,
                    flow_parent_id: flow_base_id,
                });

                const updatedCards: Card[] = response.data;
                const updatedCard = updatedCards[0];
                const updatedComboItem: ComboItem = formAnswerToComboItemFromFlow(updatedCard, flow);

                // Update the specific card in the array
                const newItemsSelected = [...itemSelected];
                newItemsSelected[cardIndex] = updatedComboItem;
                setItemSelected(newItemsSelected);
            } else {
                // If no card_id provided, update all cards in itemSelected
                const card_ids = itemSelected
                    .filter(item => item.card?.id_card !== undefined)
                    .map(item => item.card?.id_card);

                if (card_ids.length === 0) return;

                const response = await api.post(`/card/by-cards`, {
                    card_items: card_ids,
                    flow_id: flow.id_flow,
                    flow_parent_id: flow_base_id,
                });

                const updatedCards: Card[] = response.data;
                const updatedItems = updatedCards.map(card => formAnswerToComboItemFromFlow(card, flow));

                // Replace all items in itemSelected
                setItemSelected(updatedItems);
            }
        } catch (error) {
            console.log(error);
            addToast({
                type: 'error',
                title: 'Erro ao atualizar cartão',
                description: 'Ocorreu um erro ao atualizar os dados do cartão.',
            });
        }
    }, [flow, itemSelected, addToast, flow_base_id]);

    const handleOpenRegister = useCallback(async (card: Card) => {
        if (flow !== undefined) {
            openCard(card, flow, {
                onClose: () => {
                    fetchCards();
                }
            });
        }
    }, [flow, openCard, fetchCards]);

    const handleOpenRegisterById = useCallback(async (card_id: number) => {
        if (flow !== undefined) {

            const findRegister = itemSelected.filter((item) => item.card !== undefined && item.id_register === card_id);
            if (findRegister.length > 0) {
                const cardToOpen = findRegister[0].card;

                if (cardToOpen !== undefined) {
                    openCard(cardToOpen, flow, {
                        onClose: () => {
                            fetchCards();
                        }
                    });
                }
            }
        }
    }, [flow, openCard, fetchCards, itemSelected]);

    useEffect(() => {

        let newCards: Card[] = [];
        let newItemSelect: ComboItem[] = [];
        let newFlow: Flow | undefined = undefined;
        if (values !== undefined && values.length > 0) {

            for (let idx = 0; idx < values.length; idx++) {
                const item = values[idx];
                if (item.valueCardFlow !== undefined) {

                    if (idx === 0 && item.valueCardFlow.flow !== undefined) {
                        newFlow = item.valueCardFlow.flow;
                    }

                    newCards.push(item.valueCardFlow);
                }
            }

            if (newCards.length > 0 && newFlow !== undefined) {
                for (let index = 0; index < newCards.length; index++) {
                    const newComboItem: ComboItem = formAnswerToComboItemFromFlow(newCards[index], newFlow);
                    newItemSelect.push(newComboItem);
                }

                setItemSelected(newItemSelect);
                setFlow(newFlow);

            }

        }

    }, [values]);

    useEffect(() => {

        const tasksTot = itemSelected.length;
        let percent = 0;

        if (tasksTot > 0) { //Just if there is more than one tasks

            const tasksCompleted = itemSelected.filter((i) => i.complete === "S");

            if (tasksCompleted.length > 0) { //There are %
                percent = tasksCompleted.length / tasksTot;
            } else {
                percent = 0;
            }

            setPercentage(percent);

        }

    }, [itemSelected]);

    return (
        <>
            {/* Progress Bar and View Toggle */}
            {itemSelected.length > 1 ?
                <ContainerProgressBar>
                    <ProgressBarTask>
                        <ProgressBarTaskLine>
                            <ProgressBarTaskLineInside width={percentage !== undefined ? percentage * 100 : 0} />
                        </ProgressBarTaskLine>
                        <ProgressBarTaskPercent>{String(Math.round((Number(percentage) * 100)))}%</ProgressBarTaskPercent>
                    </ProgressBarTask>
                    <ViewToggleContainer>
                        <DarkTooltip title={
                            <React.Fragment>
                                {typeView === "C" ?
                                    <div>Visualizar os registros em Lista</div> :
                                    <div>Visualizar os registros em Cartões</div>
                                }
                            </React.Fragment>
                        }>
                            <ViewToggleButton type="button" onClick={handleToggleView}>
                                {typeView === "C" ? (
                                    <>
                                        <BsList size={16} />
                                    </>
                                ) : (
                                    <>
                                        <BsGrid size={16} />
                                    </>
                                )}
                            </ViewToggleButton>
                        </DarkTooltip>
                    </ViewToggleContainer>
                </ContainerProgressBar>
                :
                <></>
            }

            {/* Card View */}
            {typeView === "C" && itemSelected.sort(compareItems).map((item) => {

                const typeUser = flow !== undefined && flow.typeUserAccess !== undefined && flow.typeUserAccess !== null ? flow.typeUserAccess : "V";

                return (
                    <ItemRegister
                        key={item.id_register}
                        type={'Flow'}
                        item={item}
                        handleClick={typeUser !== "V" ? handleOpenRegisterById : undefined}
                    />
                )
            })}

            {/* List View */}
            {typeView === "L" && flow !== undefined && (
                <ComboBoxFlowTable
                    flow={flow}
                    typeUser={flow.typeUserAccess !== undefined && flow.typeUserAccess !== null ? flow.typeUserAccess : "V"}
                    cards={cards}
                    onRowClicked={handleOpenRegister}
                />
            )}
        </>
    );

}

export default ComboBoxFlowView;